import { getBlob, ref, uploadBytes } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import { caminhoDeSaida } from '@/conversas/midiaEnvio';

/**
 * Acesso à mídia das conversas — o único lugar do web que fala com o Storage.
 *
 * Baixamos os bytes com `getBlob` em vez de montar uma URL pública com
 * `getDownloadURL`, por dois motivos que andam juntos:
 *
 * 1. `getDownloadURL` simplesmente não funciona aqui. Os arquivos são gravados
 *    pelo gatilho `processarMidiaRecebida` via Admin SDK, que não cria o
 *    `firebaseStorageDownloadTokens` do qual aquela API depende — ela falha com
 *    `storage/no-download-url`.
 * 2. Mesmo se criássemos o token, a URL resultante é pública e não expira.
 *    Áudio e foto de paciente são dado sensível de saúde: quem autoriza a
 *    leitura têm de continuar sendo as `storage.rules`, que exigem membro da
 *    clínica autenticado. `getBlob` passa por elas a cada download.
 *
 * O custo é o CORS do bucket (ver `infra/cors.json`) e o fato de o blob viver
 * na memória da aba — daí `revogarUrl`, que o chamador é obrigado a usar.
 *
 * Trocar isto por URL assinada de curta duração (se vídeo grande virar rotina)
 * mexe só neste arquivo: o resto da tela conhece apenas `urlDaMidia`.
 */
export async function urlDaMidia(caminho) {
  const blob = await getBlob(ref(storage, caminho));
  return URL.createObjectURL(blob);
}

/**
 * Libera o blob. Sem isso cada mídia aberta fica na memória até o reload — numa
 * jornada inteira de atendimento, com áudio e imagem em toda conversa, o
 * acúmulo é real.
 */
export function revogarUrl(url) {
  if (url) URL.revokeObjectURL(url);
}

/**
 * Sobe o arquivo que o atendente vai enviar e devolve o CAMINHO.
 *
 * O binário não passa pela callable: ela recebe só o caminho e lê o bucket pelo
 * Admin SDK. Trafegar o arquivo pela função custaria o base64 duas vezes e
 * esbarraria no limite de payload — e ele precisa mesmo ficar no Storage,
 * porque é de lá que a conversa vai exibi-lo depois.
 *
 * Grava na pasta `saida/`, a única que as `storage.rules` abrem para o
 * navegador. A mídia recebida fica em `midia/` e continua fora de alcance.
 */
export async function subirMidiaDeSaida(clinicaId, conversaId, arquivo, nome) {
  const caminho = caminhoDeSaida(clinicaId, conversaId, nome ?? arquivo.name);
  await uploadBytes(ref(storage, caminho), arquivo, {
    contentType: arquivo.type || 'application/octet-stream',
  });
  return caminho;
}
